/**
 * The frame every portal sits inside: the rail of destinations, the firm's
 * card and the reader's, the heading over the screen, and the parsers that
 * turn a URL segment into one of this portal's destinations.
 *
 * It is role-generic by design. A portal contributes its `PortalSpec` — its
 * destinations, its copy, its accent — and this file draws the same shell
 * around any of them; `usePortalController.ts` adds the badge counts and
 * nothing else. The words the frame itself carries are in
 * `shell.constants.ts`.
 *
 * Two answers here look like one question and are not. `inboxPrincipalFor`
 * is the FIRM on an organisation's workspace, because an inbox is addressed
 * to a principal; the activity trail's viewer stays the PERSON, because a
 * trail is read by whoever signed in.
 */

import type {
  Capabilities,
  MeResponse,
  Membership,
  OrgPrivilege,
  OrgType,
  RoleKey,
  WorkspaceRef,
} from "@ondo/shared";
import { holdsPrivilege, settlementGroup, settlementState } from "@ondo/shared";
import type { ArtistSettlement, SettlementProgress } from "@ondo/shared";

import { firstNameOf, initialsOf } from "../lib/names.ts";
import { MEMBERSHIP_LEVEL_LABELS, PROFILE_HEADERS } from "../profile/profile.constants.ts";
import { workspacePath } from "../routes/routes.model.ts";
import { SHELL_COPY } from "./shell.constants.ts";
import type { PortalSpec, PortalSubject, RecordRule } from "./portal.ts";

/** Matches a Postgres uuid, which is what every record id in ONDO is. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface NavItem {
  id: string;
  label: string;
  glyph: string;
  /** Which of the sidebar's two stacks this sits in. See PortalDestination. */
  group: "main" | "foot";
  /** A real address, so the sidebar is anchors — middle-clickable, copyable. */
  href: string;
  current: boolean;
  /** Rendered as a hint beside the label, so "planned" is visible before clicking. */
  planned: boolean;
  /**
   * How many things are waiting behind this item, or null for none to say.
   *
   * Null rather than 0, because they render differently: a badge showing zero is
   * a badge that has to be read before it can be ignored.
   */
  badge: number | null;
}

/** The identity card at the foot of the sidebar. */
export interface UserCard {
  /**
   * Where the card goes — this workspace's profile.
   *
   * The card IS the entry point, as it is in both prototype portals: it carries
   * a chevron and opens the account. There is a sidebar item as well, because a
   * control whose only affordance is that it happens to be clickable is one most
   * people never press.
   */
  href: string;
  /** "MH". One letter for a single-word name, never more than two. */
  initials: string;
  name: string;
  /**
   * "Collector · Verified" on a personal workspace, and the JOB TITLE — "Art
   * Advisor" — in an organisation's.
   *
   * The two say different things because the card is answering a different
   * question in each. On a personal workspace the reader IS the subject, so
   * what matters is what the registry has confirmed about them. Inside a firm
   * the subject is the firm — its own card at the top of the rail carries the
   * verification — and what the reader needs from this one is their standing in
   * it.
   */
  meta: string;
  /**
   * "Admin · ONDO-5592" — the rung and the id, in an organisation. Null on a
   * personal workspace, where neither applies.
   *
   * The RUNG is the load-bearing half. A member of an advisory exercises the
   * firm's mandate at their own level — a viewer reads, an editor writes
   * history, an admin stands the firm down — so it decides what every screen
   * behind this rail will let them do. Somebody who cannot see their own rung
   * has no way to tell a refusal from a fault.
   */
  standing: string | null;
  /**
   * Drives the glyph and its class only. `meta` carries the same fact in words,
   * so the card still reads correctly without colour — an absent tick is
   * indistinguishable from a rendering bug, and this is a registry where being
   * verified is the product.
   *
   * False in an organisation's workspace, where the card makes no claim about
   * verification at all and the firm's card above it does.
   */
  verified: boolean;
}

/**
 * The firm above the nav, on a workspace that is an organisation.
 *
 * Null for a personal one, which is the load-bearing distinction: an
 * independent advisor's rail carries no card here because there is no entity to
 * name, and inventing one would tell them they work somewhere they do not.
 */
export interface OrgCard {
  href: string;
  initials: string;
  name: string;
  /** "ADVISORY" — the org type, in small caps. */
  kind: string;
  verified: boolean;
  /** "Verified" or "Not yet verified", in words beside the tick. */
  verifiedLabel: string;
}

export type PortalView = {
  destination: string;
  nav: NavItem[];
  title: string;
  subtitle: string;
  user: UserCard;
  /** The firm, or null on a personal workspace. */
  org: OrgCard | null;
  eyebrow: string;
  navLabel: string;
  /** The header's one action, or null when the server says the caller may not. */
  primaryAction: { label: string; href: string } | null;
  /** The header's second, quieter action, or null. See `PortalSpec.secondaryAction`. */
  secondaryAction: { label: string; href: string } | null;
  canSwitch: boolean;
  /** The role token this shell paints itself with. */
  accent: string;
  /** The copy for a destination the registry cannot answer for yet. */
  notYet: { lead: string; detail: string } | null;
  /** Where the screens below this one link to, already scoped to this workspace. */
  hrefs: {
    workBase: string;
    /** Where a destination's own screen sends a reader who needs the list. */
    recordFallback: string;
  };
};

/**
 * Null for anything that is not one of this portal's destinations.
 *
 * Exact match, with no trimming or case folding: this is a parser, and being
 * lenient here is how a hand-edited URL or a stale link ends up resolving to
 * something the caller did not ask for.
 */
export function parseDestination(spec: PortalSpec, raw: string): string | null {
  const found = spec.destinations.find((destination) => destination.id === raw);
  return found ? found.id : null;
}

/**
 * Null for anything that is not a work id.
 *
 * Exact uuid match, for the reason `parseDestination` is exact: a URL is
 * client-controlled input, and being lenient here is how a hand-edited path
 * reaches a fetch nobody asked for.
 */
export function parseRecord(raw: string | undefined): string | null {
  return raw !== undefined && UUID_PATTERN.test(raw) ? raw : null;
}

/**
 * Whether a destination takes a fourth URL segment.
 *
 * A total lookup on the spec rather than the `if` chain this replaced, so
 * adding a destination without deciding its rule is a compile error at the
 * `definePortal` call rather than a case nobody wrote.
 */
export function recordRule(spec: PortalSpec, destination: string): RecordRule {
  return spec.records[destination] ?? "none";
}

/**
 * One list drives both the sidebar and the parser, so a destination cannot be
 * routable and unlistable at once — `unlisted` is a nav decision, not a second
 * source of truth about which destinations exist.
 */
export function toNav(
  spec: PortalSpec,
  current: string,
  workspace: WorkspaceRef,
  badges: Readonly<Record<string, number | undefined>> = {},
  /**
   * The open seat's resolved privileges, or null on a personal workspace.
   *
   * OND-61. Null means there is no firm and therefore no rung and no bundle, so
   * `holdsPrivilege` answers yes — the same answer `atLeast(null)` gives and for
   * the reason stated there. Defaulted so every existing caller and every test
   * that predates the privileges keeps the rail it had.
   */
  privileges: readonly OrgPrivilege[] | null = null,
): NavItem[] {
  return spec.destinations
    .filter((destination) => destination.state !== "unlisted")
    // OND-61. A destination this seat may not open is not drawn, which is a
    // different decision from `planned` and argued where `requires` is
    // declared: a planned page explains itself, and a colleague's privilege is
    // not ours to advertise. The route stays addressable and the screen behind
    // it refuses on its own, from the database.
    .filter(
      (destination) =>
        destination.requires === undefined ||
        holdsPrivilege(privileges, destination.requires),
    )
    .map((destination) => ({
      id: destination.id,
      // `titleFor` rather than the constant, because one destination's name is
      // not fixed: "My Profile" is the wrong label in a firm's workspace, where
      // the screen behind it is the firm's record and its staff.
      label: titleFor(spec, destination.id, workspace),
      glyph: destination.glyph,
      group: destination.group,
      href: workspacePath(workspace, destination.id),
      current: destination.id === current,
      planned: destination.state === "planned",
      badge: badges[destination.id] || null,
    }));
}

/**
 * The role's colour, as a custom property name rather than a hex literal.
 *
 * Same convention as `src/login/roles.ts`. Keeping it a token means the shell
 * is role-generic — a portal reuses this stylesheet by passing a different
 * accent — and no feature stylesheet names a colour.
 */
export function accentFor(role: RoleKey): string {
  return `var(--role-${role})`;
}

export function toUserCard(
  principal: MeResponse["principal"],
  roleLabel: string,
  workspace: WorkspaceRef,
  spec: PortalSpec,
  membership: Membership | null,
): UserCard {
  // A portal with no profile screen still needs somewhere for the card to go,
  // and its own front page is the honest answer — a dead card would read as a
  // broken one.
  const href = workspacePath(workspace, spec.profileAt ?? spec.defaultDestination);
  const common = { href, initials: initialsOf(principal.displayName), name: principal.displayName };

  if (!workspace.organisation) {
    return {
      ...common,
      meta: `${roleLabel} · ${
        principal.verified ? SHELL_COPY.verified : SHELL_COPY.unverified
      }`,
      standing: null,
      verified: principal.verified,
    };
  }

  return {
    ...common,
    // The job title falls back to the role, because `title` is free text an
    // admin may simply not have filled in — and a blank line under a name reads
    // as a fault rather than as an absence.
    meta: membership?.title ?? workspace.title ?? roleLabel,
    standing: membership
      ? `${MEMBERSHIP_LEVEL_LABELS[membership.level]} · ${principal.ondoId}`
      : principal.ondoId,
    verified: false,
  };
}

/**
 * The firm's card, or null on a personal workspace.
 *
 * The organisation's OWN verified flag, never the reader's: a verified advisor
 * at an unverified firm must not make the firm look checked, which is the whole
 * claim the tick makes on a registry whose product is verification.
 */
export function toOrgCard(
  workspace: WorkspaceRef,
  spec: PortalSpec,
  kindLabel: (orgType: OrgType) => string,
): OrgCard | null {
  const organisation = workspace.organisation;
  if (!organisation) return null;

  return {
    href: workspacePath(workspace, spec.profileAt ?? spec.defaultDestination),
    initials: initialsOf(organisation.displayName),
    name: organisation.displayName,
    kind: kindLabel(organisation.orgType),
    verified: organisation.verified,
    verifiedLabel: organisation.verified ? SHELL_COPY.verified : SHELL_COPY.unverified,
  };
}

/**
 * The heading over a destination.
 *
 * Fixed for every screen but the profile, whose SUBJECT follows the workspace —
 * a personal one opens onto the reader, an organisation's onto the entity — so
 * a header reading "My Profile" over a firm's record and its staff list would
 * be describing the wrong thing entirely. `PROFILE_HEADERS` holds both, beside
 * the screen that knows why there are two.
 */
export function titleFor(
  spec: PortalSpec,
  destination: string,
  workspace: WorkspaceRef,
): string {
  if (destination !== spec.profileAt) {
    return spec.headers[destination]?.title ?? "";
  }

  return PROFILE_HEADERS[workspace.organisation ? "organisation" : "person"].title;
}

/**
 * The line under the title.
 *
 * Every destination's is a fixed sentence about the screen, except two. The
 * greeting destination's greets whoever is reading it — the one place a name
 * belongs. The profile's follows its subject, for the reason `titleFor` gives.
 */
export function subtitleFor(
  spec: PortalSpec,
  destination: string,
  principal: MeResponse["principal"],
  workspace: WorkspaceRef,
): string {
  if (destination === spec.profileAt) {
    return PROFILE_HEADERS[workspace.organisation ? "organisation" : "person"].subtitle;
  }

  if (destination !== spec.greetsAt) {
    const subtitle = spec.headers[destination]?.subtitle ?? "";
    // The subject is the ENTITY on an organisation's workspace and the reader on
    // a personal one — the same distinction `titleFor` makes about the profile,
    // and for the same reason: a member of an advisory reads screens about the
    // firm's mandates, not their own.
    return typeof subtitle === "function" ? subtitle(subjectOf(principal, workspace)) : subtitle;
  }

  const first = firstNameOf(principal.displayName);
  return first === null ? SHELL_COPY.welcome : `${SHELL_COPY.welcome}, ${first}`;
}

/**
 * WHOSE inbox this workspace opens — the firm's, or the reader's own.
 *
 * `Verifications.tsx` states the rule already: a request addressed to a gallery
 * somebody works at belongs in that gallery's inbox and not in their personal
 * one, because the two are different subjects and answering for the first
 * commits the organisation's name. The collector shell stated it too, in a
 * comment, and then passed `principal.ondoId` unconditionally — which was
 * harmless only for as long as no organisation-backed workspace was built.
 * The agent portal is the first one, so the rule needs a function rather than a
 * paragraph.
 *
 * NOT the same answer as `Activity`'s `viewerOndoId`, which stays the PERSON's
 * in every workspace. An inbox is addressed to a principal; a trail is read by a
 * reader, and the reader signing in is always a person. Giving both the same id
 * would either put the firm's requests in a member's inbox or make the firm's
 * own acts read as "You".
 */
export function inboxPrincipalFor(
  principal: MeResponse["principal"],
  workspace: WorkspaceRef,
): string {
  return workspace.organisation?.ondoId ?? principal.ondoId;
}

/** Whose workspace this is: the organisation, or the reader when there is none. */
export function subjectOf(
  principal: MeResponse["principal"],
  workspace: WorkspaceRef,
): PortalSubject {
  return workspace.organisation
    ? { name: workspace.organisation.displayName, ondoId: workspace.organisation.ondoId }
    : { name: principal.displayName, ondoId: principal.ondoId };
}

export function resolvePortal(input: {
  spec: PortalSpec;
  destination: string;
  workspace: WorkspaceRef;
  principal: MeResponse["principal"];
  capabilities: Capabilities;
  role: RoleKey;
  roleLabel: string;
  canSwitch: boolean;
  orgKindLabel: (orgType: OrgType) => string;
  /** The caller's membership of this workspace's organisation, or null. */
  membership: Membership | null;
  /** Counts to hang on nav items. Empty when nothing is waiting anywhere. */
  badges?: Readonly<Record<string, number | undefined>>;
}): PortalView {
  const { spec, workspace } = input;
  const action = spec.primaryAction(input.capabilities, input.destination);
  const secondary = spec.secondaryAction(input.capabilities, input.destination);

  return {
    destination: input.destination,
    // OND-61. The open seat's privileges decide which destinations are drawn.
    // Null on a personal workspace, where there is no seat and no bundle, and
    // `holdsPrivilege` answers yes.
    nav: toNav(
      spec,
      input.destination,
      workspace,
      input.badges,
      input.membership?.privileges ?? null,
    ),
    title: titleFor(spec, input.destination, workspace),
    subtitle: subtitleFor(spec, input.destination, input.principal, workspace),
    user: toUserCard(input.principal, input.roleLabel, workspace, spec, input.membership),
    org: toOrgCard(workspace, spec, input.orgKindLabel),
    eyebrow: spec.eyebrow,
    navLabel: spec.navLabel,
    primaryAction: action
      ? { label: action.label, href: workspacePath(workspace, action.destination) }
      : null,
    secondaryAction: secondary
      ? {
          label: secondary.label,
          href:
            workspacePath(workspace, secondary.destination) +
            (secondary.search ? `?${secondary.search}` : ""),
        }
      : null,
    canSwitch: input.canSwitch,
    accent: accentFor(input.role),
    notYet: spec.notYet[input.destination] ?? null,
    hrefs: {
      workBase: workspacePath(workspace, "work"),
      recordFallback: workspacePath(workspace, spec.recordFallback),
    },
  };
}

/**
 * What the rail's settlements badge counts: rows still waiting on the gallery.
 *
 * The `due` group — due, collected-not-remitted, and awaiting a rate — and
 * never the closed or void ones. Same grouping the settlements screen draws,
 * so the badge and the screen cannot disagree about how many are open.
 */
export function dueSettlementCount(rows: readonly SettlementProgress[]): number {
  return rows.filter((row) => settlementGroup(settlementState(row)) === "due").length;
}

/**
 * The artist's side of the same badge: remittances not yet confirmed or
 * disputed. A void settlement asks nothing, whatever else is on the row.
 */
export function unansweredRemittanceCount(
  rows: readonly Pick<ArtistSettlement, "remittedAt" | "artistAnsweredAt" | "voidedAt">[],
): number {
  return rows.filter(
    (row) => row.remittedAt !== null && row.artistAnsweredAt === null && row.voidedAt === null,
  ).length;
}

/** Requests nobody has answered yet — the verifications badge. */
export function awaitingVerificationCount(
  requests: readonly { outcome: string | null }[],
): number {
  return requests.filter((request) => request.outcome === null).length;
}
